import App from './App';
import Spinner from './components/base/Spinner';
import DirectAccessBlocked from './components/modules/DirectAccessBlocked';
import useGetRefreshToken from './hooks/useGetRefreshToken';
import { isDirectAccessBlocked } from './utils/embeddedAccess';

/**
 * The boot gate.
 *
 * Direct access is refused here rather than on a route, because a route can be
 * navigated past and this cannot: when the gate closes, the router never
 * mounts, so there is no /login to reach and no screen to deep-link into.
 *
 * Session bootstrap lives in the inner component on purpose. Returning early
 * from this one leaves useGetRefreshToken unmounted, so a blocked visitor makes
 * no refresh call and no handoff request -- they are turned away before the app
 * talks to the API at all.
 *
 * @returns {JSX.Element} the blocked notice, or the booting app
 */
export default function AppWrapper() {
    if (isDirectAccessBlocked()) {
        return <DirectAccessBlocked />;
    }

    return <BootstrappedApp />;
}

/**
 * Establish a session, then hand over to the app.
 *
 * @returns {JSX.Element} a spinner while booting, then the app
 */
function BootstrappedApp() {
    const { isProcessing } = useGetRefreshToken();

    if (isProcessing) {
        return (
            <div className='h-100 w-100 flex-centered'>
                <Spinner isCentered={true} size={30} />;
            </div>
        );
    }

    return <App />;
}
